import React, { useState } from 'react';
import { FileDown, Building2, Calendar, Users, FileText, MapPin, Building, User, BarChart } from 'lucide-react';
import ExcelJS from 'exceljs';
import './AppointmentOrderForm.css';

// Initialize empty form data - will be populated from API or manual entry
const emptyFormData = {
  // Establishment Details
  est_name: '',
  est_location: '',

  // Site Address (Establishment Location)
  site_name: '',
  site_add1: '',
  site_add2: '',
  site_add3: '',
  site_city: '',
  site_pincode: '',

  // Registered Head Office Address
  reg_ho_location: '',
  reg_ho_add1: '',
  reg_ho_add2: '',
  reg_ho_add3: '',
  reg_ho_city: '',
  reg_phone: '',
  reg_fax: '',
  reg_email: '',

  // Employer Details
  emp_address: '',
  emp_add1: '',
  emp_add2: '',
  emp_add3: '',
  emp_city: '',
  emp_st: '',
  emp_pincode: '',
  emp_desig: '',
  emp_phone: '',
  emp_fax: '',
  emp_email: '',

  // Manager Details
  emp_manager: '',
  emp_m_add1: '',
  emp_m_city: '',
  emp_m_pincode: '',
  emp_m_ph: '',
  emp_m_email: '',

  // Employment Statistics (Section 6-A)
  no_roll_beg: '',
  no_roll_end: '',
  no_days_worked: '',
  no_m_w: '',
  no_m_ow: '',
  tot_wage_men: '',
  tot_wage_women: '',
  to_wages: '',

  // Earned Leave - Men (Section 7)
  no_m_el_emp: '',
  no_m_el_elg: '',
  no_m_el_avl: '',
  no_m_p_el: '',

  // Earned Leave - Women (Section 7)
  no_w_el_emp: '',
  no_w_el_elg: '',
  no_w_el_avl: '',
  no_w_p_el: '',

  // Signatory
  Manager_Name: ''
};

const FormUAnnualReturns = () => {
  const [formData, setFormData] = useState(emptyFormData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('organization');

  const tabs = [
    { id: 'organization', label: 'Organization', icon: Building2 },
    { id: 'site', label: 'Site Address', icon: MapPin },
    { id: 'office', label: 'Head Office', icon: Building },
    { id: 'employer', label: 'Employer', icon: User },
    { id: 'manager', label: 'Manager', icon: Users },
    { id: 'employment', label: 'Statistics', icon: BarChart },
    { id: 'leave-men', label: 'Leave (Men)', icon: Calendar },
    { id: 'leave-women', label: 'Leave (Women)', icon: Calendar },
    { id: 'review', label: 'Review', icon: FileText }
  ];

  // Fetch form data from API on component mount
  React.useEffect(() => {
    const fetchFormData = async () => {
      setIsLoadingData(true);
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        console.log('Fetching form data from local API');
        const response = await fetch(`${baseUrl}/api/forms/form_u_annual?establishment_id=1&year=${new Date().getFullYear()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });

        if (!response.ok) {
          throw new Error(`API request failed with status: ${response.status}`);
        }

        const apiData = await response.json();
        console.log('API data received:', apiData);
        console.log('Total fields received:', Object.keys(apiData).length);

        // Map API data to form fields
        setFormData(apiData);
        console.log('Form fields populated with API data');

      } catch (error) {
        console.error('Error fetching form data:', error);
        console.log('Form will remain empty. User can fill manually.');
        // Keep empty form data if API fails
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      // Step 1: Load template file
      console.log('Loading template from /templates/Form-U-Annual-Returns.xlsx...');
      const templateResponse = await fetch('/templates/Form-U-Annual-Returns.xlsx');
      const arrayBuffer = await templateResponse.arrayBuffer();

      // Step 2: Create ExcelJS workbook and load template
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      console.log('\n=== REPLACING PLACEHOLDERS WITH FORM DATA ===\n');

      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/g;
      let replacementCount = 0;

      // Step 3: Iterate through all worksheets and replace placeholders
      workbook.eachSheet((worksheet) => {
        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellValue = cell.value ? String(cell.value) : '';

            if (placeholderRegex.test(cellValue)) {
              placeholderRegex.lastIndex = 0;

              let newValue = cellValue;
              let match;

              while ((match = placeholderRegex.exec(cellValue)) !== null) {
                const placeholder = match[0];
                const varName = match[1];

                // Use form data for replacement (which is populated from API or user input)
                let replacementValue = formData[varName] !== undefined ? formData[varName] : '';

                const cellAddress = cell.address;
                console.log(`Cell ${cellAddress}: "${placeholder}" → "${replacementValue}"`);

                newValue = newValue.replace(placeholder, replacementValue);
                replacementCount++;
              }

              placeholderRegex.lastIndex = 0;
              cell.value = newValue;
            }
          });
        });
      });

      console.log(`\n=== REPLACEMENT COMPLETE: ${replacementCount} placeholders replaced ===\n`);

      // Step 4: Generate Form file as buffer
      const buffer = await workbook.xlsx.writeBuffer();

      // Step 5: Create blob and download
      const year = new Date().getFullYear();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-U-Annual-Returns-${year}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setTimeout(() => {
        setIsGenerating(false);
        alert('Form U generated successfully!');
      }, 500);
    } catch (error) {
      console.error('Error generating Form U:', error);
      setIsGenerating(false);
      alert(`Error: ${error.message}\n\nPlease check the console for more details.`);
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form U - Combined Annual Return</h2>
          <p className="appointment-subtitle">Karnataka Shops & Commercial Organization Rules, 1963</p>
        </div>
      </div>

      <div className="appointment-content">
        <div className="info-alert">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>
            {isLoadingData
              ? 'Loading form data from API...'
              : 'Form data loaded from API. Review and edit fields if needed, then click Generate Form on the Review tab.'}
          </span>
        </div>

        {/* Tab Navigation */}
        <div className="tabs-container">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Tab 1: Establishment Details */}
          {activeTab === 'organization' && (
            <div className="form-section">
              <div className="section-header">
                <Building2 size={20} />
                <h3>Organization Details</h3>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Organization Name *</label>
                  <input type="text" value={formData.est_name} onChange={(e) => handleInputChange('est_name', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Organization Location *</label>
                  <input type="text" value={formData.est_location} onChange={(e) => handleInputChange('est_location', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Site Address */}
          {activeTab === 'site' && (
            <div className="form-section">
              <div className="section-header">
                <MapPin size={20} />
                <h3>Site Address (Organization Location)</h3>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Site Name</label>
                  <input type="text" value={formData.site_name} onChange={(e) => handleInputChange('site_name', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 1</label>
                  <input type="text" value={formData.site_add1} onChange={(e) => handleInputChange('site_add1', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 2</label>
                  <input type="text" value={formData.site_add2} onChange={(e) => handleInputChange('site_add2', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 3</label>
                  <input type="text" value={formData.site_add3} onChange={(e) => handleInputChange('site_add3', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>City</label>
                  <input type="text" value={formData.site_city} onChange={(e) => handleInputChange('site_city', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <input type="text" value={formData.site_pincode} onChange={(e) => handleInputChange('site_pincode', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Tab 3: Registered Head Office */}
          {activeTab === 'office' && (
            <div className="form-section">
              <div className="section-header">
                <Building size={20} />
                <h3>Registered Head Office Address</h3>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>HO Location</label>
                  <input type="text" value={formData.reg_ho_location} onChange={(e) => handleInputChange('reg_ho_location', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 1</label>
                  <input type="text" value={formData.reg_ho_add1} onChange={(e) => handleInputChange('reg_ho_add1', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 2</label>
                  <input type="text" value={formData.reg_ho_add2} onChange={(e) => handleInputChange('reg_ho_add2', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 3</label>
                  <input type="text" value={formData.reg_ho_add3} onChange={(e) => handleInputChange('reg_ho_add3', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>City</label>
                  <input type="text" value={formData.reg_ho_city} onChange={(e) => handleInputChange('reg_ho_city', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input type="text" value={formData.reg_phone} onChange={(e) => handleInputChange('reg_phone', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Fax</label>
                  <input type="text" value={formData.reg_fax} onChange={(e) => handleInputChange('reg_fax', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Email</label>
                  <input type="email" value={formData.reg_email} onChange={(e) => handleInputChange('reg_email', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Tab 4: Employer Details */}
          {activeTab === 'employer' && (
            <div className="form-section">
              <div className="section-header">
                <User size={20} />
                <h3>Employer Details</h3>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Employer Name</label>
                  <input type="text" value={formData.emp_address} onChange={(e) => handleInputChange('emp_address', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Designation</label>
                  <input type="text" value={formData.emp_desig} onChange={(e) => handleInputChange('emp_desig', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 1</label>
                  <input type="text" value={formData.emp_add1} onChange={(e) => handleInputChange('emp_add1', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 2</label>
                  <input type="text" value={formData.emp_add2} onChange={(e) => handleInputChange('emp_add2', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address Line 3</label>
                  <input type="text" value={formData.emp_add3} onChange={(e) => handleInputChange('emp_add3', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>City</label>
                  <input type="text" value={formData.emp_city} onChange={(e) => handleInputChange('emp_city', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>State</label>
                  <input type="text" value={formData.emp_st} onChange={(e) => handleInputChange('emp_st', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <input type="text" value={formData.emp_pincode} onChange={(e) => handleInputChange('emp_pincode', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input type="text" value={formData.emp_phone} onChange={(e) => handleInputChange('emp_phone', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Fax</label>
                  <input type="text" value={formData.emp_fax} onChange={(e) => handleInputChange('emp_fax', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Email</label>
                  <input type="email" value={formData.emp_email} onChange={(e) => handleInputChange('emp_email', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Tab 5: Manager Details */}
          {activeTab === 'manager' && (
            <div className="form-section">
              <div className="section-header">
                <Users size={20} />
                <h3>Manager Details</h3>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Manager Name</label>
                  <input type="text" value={formData.emp_manager} onChange={(e) => handleInputChange('emp_manager', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Address</label>
                  <input type="text" value={formData.emp_m_add1} onChange={(e) => handleInputChange('emp_m_add1', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>City</label>
                  <input type="text" value={formData.emp_m_city} onChange={(e) => handleInputChange('emp_m_city', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <input type="text" value={formData.emp_m_pincode} onChange={(e) => handleInputChange('emp_m_pincode', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input type="text" value={formData.emp_m_ph} onChange={(e) => handleInputChange('emp_m_ph', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Email</label>
                  <input type="email" value={formData.emp_m_email} onChange={(e) => handleInputChange('emp_m_email', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Tab 6: Employment Statistics */}
          {activeTab === 'employment' && (
            <div className="form-section">
              <div className="section-header">
                <BarChart size={20} />
                <h3>Employment Statistics</h3>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Persons on Roll (Beginning of Year)</label>
                  <input type="text" value={formData.no_roll_beg} onChange={(e) => handleInputChange('no_roll_beg', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Persons on Roll (End of Year)</label>
                  <input type="text" value={formData.no_roll_end} onChange={(e) => handleInputChange('no_roll_end', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Number of Days Worked</label>
                  <input type="text" value={formData.no_days_worked} onChange={(e) => handleInputChange('no_days_worked', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Man Days Worked - Men</label>
                  <input type="text" value={formData.no_m_w} onChange={(e) => handleInputChange('no_m_w', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Man Hours Worked - Men</label>
                  <input type="text" value={formData.no_m_ow} onChange={(e) => handleInputChange('no_m_ow', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Total Wages Paid to Men</label>
                  <input type="text" value={formData.tot_wage_men} onChange={(e) => handleInputChange('tot_wage_men', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Total Wages Paid to Women</label>
                  <input type="text" value={formData.tot_wage_women} onChange={(e) => handleInputChange('tot_wage_women', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Total Wages (All Employees)</label>
                  <input type="text" value={formData.to_wages} onChange={(e) => handleInputChange('to_wages', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Tab 7: Earned Leave - Men */}
          {activeTab === 'leave-men' && (
            <div className="form-section">
              <div className="section-header">
                <Calendar size={20} />
                <h3>Earned Leave - Men</h3>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Total Employed</label>
                  <input type="text" value={formData.no_m_el_emp} onChange={(e) => handleInputChange('no_m_el_emp', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Eligible for Leave</label>
                  <input type="text" value={formData.no_m_el_elg} onChange={(e) => handleInputChange('no_m_el_elg', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Leave Availed</label>
                  <input type="text" value={formData.no_m_el_avl} onChange={(e) => handleInputChange('no_m_el_avl', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Paid in Lieu of Leave</label>
                  <input type="text" value={formData.no_m_p_el} onChange={(e) => handleInputChange('no_m_p_el', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Tab 8: Earned Leave - Women */}
          {activeTab === 'leave-women' && (
            <div className="form-section">
              <div className="section-header">
                <Calendar size={20} />
                <h3>Earned Leave - Women</h3>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Total Employed</label>
                  <input type="text" value={formData.no_w_el_emp} onChange={(e) => handleInputChange('no_w_el_emp', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Eligible for Leave</label>
                  <input type="text" value={formData.no_w_el_elg} onChange={(e) => handleInputChange('no_w_el_elg', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Leave Availed</label>
                  <input type="text" value={formData.no_w_el_avl} onChange={(e) => handleInputChange('no_w_el_avl', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Paid in Lieu of Leave</label>
                  <input type="text" value={formData.no_w_p_el} onChange={(e) => handleInputChange('no_w_p_el', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Tab 9: Review & Generate */}
          {activeTab === 'review' && (
            <div>
              <div className="form-section">
                <div className="section-header">
                  <FileText size={20} />
                  <h3>Signatory & Review</h3>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Manager Name (Signatory)</label>
                    <input type="text" value={formData.Manager_Name} onChange={(e) => handleInputChange('Manager_Name', e.target.value)} />
                  </div>
                </div>

                <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'var(--theme-surface)', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>Summary</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
                    <div><strong>Organization:</strong> {formData.est_name}</div>
                    <div><strong>Location:</strong> {formData.est_location}</div>
                    <div><strong>Persons on Roll:</strong> {formData.no_roll_beg} → {formData.no_roll_end}</div>
                    <div><strong>Days Worked:</strong> {formData.no_days_worked}</div>
                    <div><strong>Total Wages:</strong> {formData.to_wages}</div>
                    <div><strong>Signatory:</strong> {formData.Manager_Name}</div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button onClick={generateExcel} disabled={isGenerating} className={`btn-generate ${isGenerating ? 'generating' : ''}`}>
                  {isGenerating ? (
                    <>
                      <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" opacity="0.25"/>
                        <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                      </svg>
                      <span>Generating Form U...</span>
                    </>
                  ) : (
                    <>
                      <FileDown size={20} />
                      <span>Generate & Download Excel</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormUAnnualReturns;
